import React from 'react';
import { useInView, useMotionValue, useReducedMotion, animate, useMotionValueEvent } from 'framer-motion';
import { formatCount } from '../../service/impactStats';

// Animates a number counting up from 0 to `value` once it scrolls into
// view, then never again (once: true) — re-triggering every time someone
// scrolls past it would be distracting, not delightful.
//
// Writes the formatted number straight into a ref'd span on every motion
// tick instead of via React state, so a fast 1.4s count-up doesn't cause
// 60+ re-renders of the surrounding component tree — the number is the
// only thing that needs to update per frame.
//
// Respects prefers-reduced-motion: jumps straight to the final value with
// no animation, per the site's motion accessibility rule.
export default function CountUpNumber({ value, suffix = '', prefix = '', duration = 1.4 }) {
  const ref = React.useRef(null);
  const spanRef = React.useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const reduceMotion = useReducedMotion();
  const motionValue = useMotionValue(0);

  useMotionValueEvent(motionValue, 'change', (latest) => {
    if (spanRef.current) spanRef.current.textContent = formatCount(latest);
  });

  React.useEffect(() => {
    if (!inView) return undefined;

    if (reduceMotion) {
      if (spanRef.current) spanRef.current.textContent = formatCount(value);
      return undefined;
    }

    const controls = animate(motionValue, value, { duration, ease: [0.22, 1, 0.36, 1] });
    return () => controls.stop();
  }, [inView, reduceMotion, value, duration, motionValue]);

  return (
    <span ref={ref}>
      {prefix}
      <span ref={spanRef}>0</span>
      {suffix}
    </span>
  );
}
